import { useState, useCallback, useEffect, useRef } from "react";
import { useWorkoutDataWithPersistence } from "./useWorkoutDataWithPersistence";
import { useWorkoutSessionPersistence } from "./useWorkoutSessionPersistence";
import { Exercise } from "../types";

export const useWorkout = (routineId: string | undefined) => {
  // Use the new persistence-aware data loading
  const { routine, isLoading, error } = useWorkoutDataWithPersistence({ routineId });
  
  // Session persistence
  const { 
    getSession, 
    saveSession, 
    clearSession, 
    hasActiveSession,
    getSessionAge 
  } = useWorkoutSessionPersistence(routineId);
  
  const [workoutStarted, setWorkoutStarted] = useState(false);
  const [activeExerciseId, setActiveExerciseId] = useState<string | null>(null);
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [completedSets, setCompletedSets] = useState<{ [key: string]: boolean[] }>({});
  const [showCompletionCheck, setShowCompletionCheck] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [isExerciseRest, setIsExerciseRest] = useState(false);
  
  // Resume dialog state
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const hasCheckedSession = useRef(false);

  const exercises = routine?.exercises as Exercise[] || [];

  // Check for existing session when routine loads - only once
  useEffect(() => {
    if (routine && !workoutStarted && !showResumeDialog && !hasCheckedSession.current) {
      hasCheckedSession.current = true;
      const existingSession = getSession();
      if (existingSession) {
        console.log('📂 Found existing workout session:', existingSession);
        setShowResumeDialog(true);
      }
    }
  }, [routine, workoutStarted]);

  // Save session whenever workout state changes
  useEffect(() => {
    if (workoutStarted && activeExerciseId) {
      saveSession({
        startedAt: getSession()?.startedAt || new Date().toISOString(),
        activeExerciseId,
        currentSetIndex,
        completedSets,
      });
    }
  }, [workoutStarted, activeExerciseId, currentSetIndex, completedSets, saveSession, getSession]);

  const handleResumeWorkout = useCallback(() => {
    // Always close dialog first to prevent UI blocking
    setShowResumeDialog(false);
    
    const session = getSession();
    if (session && exercises.length > 0) {
      setWorkoutStarted(true);
      setActiveExerciseId(session.activeExerciseId);
      setCurrentSetIndex(session.currentSetIndex);
      setCompletedSets(session.completedSets);
      
      console.log('▶️ Resumed workout from session:', {
        activeExerciseId: session.activeExerciseId,
        currentSetIndex: session.currentSetIndex,
        completedSetsCount: Object.values(session.completedSets).flat().filter(Boolean).length
      });
    } else {
      // Clear corrupted session if exercises aren't loaded
      clearSession();
      console.log('⚠️ Session cleared - exercises not available');
    }
  }, [getSession, exercises, clearSession]);

  const handleStartFresh = useCallback(() => {
    // Always close dialog first
    setShowResumeDialog(false);
    clearSession();
    console.log('🔄 Starting fresh, session cleared');
  }, [clearSession]);

  const handleStartWorkout = useCallback(() => {
    if (exercises.length > 0) {
      setWorkoutStarted(true);
      setActiveExerciseId(exercises[0].id);
      setCurrentSetIndex(0);
      
      // Initialize completed sets structure
      const initialCompletedSets: { [key: string]: boolean[] } = {};
      exercises.forEach(exercise => {
        initialCompletedSets[exercise.id] = new Array(exercise.sets).fill(false);
      });
      setCompletedSets(initialCompletedSets);
      
      console.log('🏋️ Workout started with enhanced persisted data:', {
        totalExercises: exercises.length,
        firstExercise: exercises[0],
        hasPersistedData: exercises[0]?.set_data?.length > 0
      });
    }
  }, [exercises]);

  const handleSetComplete = useCallback((exerciseId: string, setIndex: number) => {
    console.log('🎯 handleSetComplete called:', { exerciseId, setIndex });
    
    // Update completed sets
    setCompletedSets(prev => {
      const newCompletedSets = {
        ...prev,
        [exerciseId]: prev[exerciseId]?.map((completed, index) => 
          index === setIndex ? true : completed
        ) || []
      };
      
      console.log('🎯 Updated completed sets:', newCompletedSets);
      
      // Calculate if all sets are completed with the NEW state
      const updatedExerciseSets = newCompletedSets[exerciseId] || [];
      const allSetsCompleted = updatedExerciseSets.every(completed => completed);
      
      console.log('🎯 All sets completed?', allSetsCompleted, 'Sets:', updatedExerciseSets);
      
      // Check if ALL exercises are completed (workout finished)
      const allExercisesCompleted = exercises.every(ex => {
        const sets = newCompletedSets[ex.id] || [];
        return sets.every(completed => completed);
      });
      
      if (allExercisesCompleted) {
        // Workout complete! Clear the session
        console.log('🎉 Workout complete! Clearing session...');
        clearSession();
      }
      
      // Only show completion check animation when ALL sets of the exercise are completed
      if (allSetsCompleted) {
        setShowCompletionCheck(true);
        
        setTimeout(() => {
          setShowCompletionCheck(false);
          
          // All sets of this exercise are completed
          const currentExerciseIndex = exercises.findIndex(ex => ex.id === exerciseId);
          const isLastExercise = currentExerciseIndex === exercises.length - 1;
          
          console.log('🎯 Exercise completed! Current index:', currentExerciseIndex, 'Is last?', isLastExercise);
          
          if (!isLastExercise) {
            // Move to next exercise and start exercise rest timer
            const nextExercise = exercises[currentExerciseIndex + 1];
            setActiveExerciseId(nextExercise.id);
            setCurrentSetIndex(0);
            setIsExerciseRest(true);
            setShowTimer(true);
            console.log('🏃 Moving to next exercise:', nextExercise.name);
          }
        }, 1500);
      } else {
        // Move to next set and start set rest timer immediately (no completion check)
        const nextSetIndex = setIndex + 1;
        setCurrentSetIndex(nextSetIndex);
        setIsExerciseRest(false);
        setShowTimer(true);
        console.log('⏭️ Moving to next set:', nextSetIndex);
      }
      
      return newCompletedSets;
    });
  }, [exercises, clearSession]);

  const handleTimerComplete = useCallback(() => {
    setShowTimer(false);
    setIsExerciseRest(false);
    console.log('⏰ Timer completed');
  }, []);

  const handleTimerSkip = useCallback(() => {
    setShowTimer(false);
    setIsExerciseRest(false);
    console.log('⏰ Timer skipped');
  }, []);

  const getActiveExercise = useCallback(() => {
    return exercises.find(exercise => exercise.id === activeExerciseId);
  }, [exercises, activeExerciseId]);

  const handleExerciseSelect = useCallback((exerciseId: string) => {
    setActiveExerciseId(exerciseId);
    setCurrentSetIndex(0);
    setShowTimer(false);
    setIsExerciseRest(false);
    console.log('🎯 Exercise selected:', exerciseId);
  }, []);

  const handleStopWorkout = useCallback(() => {
    // Don't clear session on stop - user might want to resume later
    setWorkoutStarted(false);
    setActiveExerciseId(null);
    setCurrentSetIndex(0);
    setShowTimer(false);
    setShowCompletionCheck(false);
    setIsExerciseRest(false);
    console.log('🛑 Workout stopped, session preserved for later resume');
  }, []);

  return {
    routine,
    isLoading: isLoading || !routine,
    error,
    workoutStarted,
    activeExerciseId,
    currentSetIndex,
    completedSets,
    showCompletionCheck,
    showTimer,
    isExerciseRest,
    showResumeDialog,
    sessionAge: getSessionAge(),
    handleStartWorkout,
    handleSetComplete,
    handleTimerComplete,
    handleTimerSkip,
    getActiveExercise,
    handleExerciseSelect,
    handleStopWorkout,
    handleResumeWorkout,
    handleStartFresh,
  };
};
